import { useCallback } from 'react';
import { Pages } from '../shared';
import { type UploadedFile } from './useUploadedFiles';

type UseFileStatusTextProps = {
  statusCounts: {
    processing: number;
    ready: number;
    completed: number;
    error: number;
  };
  totalFilesUploaded: number;
  currentPageNumber: Pages;
  areAnyFilesProcessingAndNotReady: boolean;
};

type UseFileStatusTextReturn = {
  getFileStatusText: (selectedFile: UploadedFile) => string | undefined;
};

export function useFileStatusText({
  statusCounts,
  totalFilesUploaded,
  currentPageNumber,
  areAnyFilesProcessingAndNotReady,
}: UseFileStatusTextProps): UseFileStatusTextReturn {
  const { processing: numberOfFilesProcessing, error: numberOfFilesErrored } =
    statusCounts;
  const areAnyFilesProcessingOrHaveErrors =
    numberOfFilesProcessing > 0 || numberOfFilesErrored > 0;

  const getProcessingOrErrorsText = useCallback(() => {
    const fileOrFilesText = (count: number) => (count === 1 ? 'file' : 'files');

    if (areAnyFilesProcessingOrHaveErrors) {
      if (numberOfFilesProcessing > 0 && numberOfFilesErrored > 0) {
        return `(${numberOfFilesProcessing} ${fileOrFilesText(
          numberOfFilesProcessing,
        )} processing, ${numberOfFilesErrored} ${fileOrFilesText(
          numberOfFilesErrored,
        )} failed)`;
      }
      if (numberOfFilesProcessing > 0) {
        return `(${numberOfFilesProcessing} ${fileOrFilesText(
          numberOfFilesProcessing,
        )} processing)`;
      }
      if (numberOfFilesErrored > 0) {
        return `(${numberOfFilesErrored} ${fileOrFilesText(
          numberOfFilesErrored,
        )} failed to process)`;
      }
    }
    return '';
  }, [
    areAnyFilesProcessingOrHaveErrors,
    numberOfFilesProcessing,
    numberOfFilesErrored,
  ]);

  const getFileStatusText = useCallback(
    (selectedFile: UploadedFile) => {
      const showOnPage =
        (currentPageNumber === Pages.EDIT_ENTRIES_PAGE &&
          !areAnyFilesProcessingAndNotReady) ||
        currentPageNumber === Pages.REVIEW_PAGE;
      if (!showOnPage || selectedFile?.orderNumber == null) return undefined;

      const processingOrErrorsText = getProcessingOrErrorsText();
      return `File ${selectedFile.orderNumber} of ${totalFilesUploaded}${
        processingOrErrorsText ? ` ${processingOrErrorsText}` : ''
      }`;
    },
    [
      currentPageNumber,
      totalFilesUploaded,
      getProcessingOrErrorsText,
      areAnyFilesProcessingAndNotReady,
    ],
  );

  return {
    getFileStatusText,
  };
}
